import React, { Dispatch, FC, MutableRefObject, ReactElement, SetStateAction, useCallback, useContext, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { displayError } from '../../../../../React/Common/Utils/Messages'
import { getNewUIConfig, NewUIConfigDTO } from '../../../../../React/Common/API/GenericConfigAPI'
import { JSONResponse } from '../../../../../NonReact/common/Types/Types'
import { EMPTY_STRING } from '../../../../../NonReact/constants/Common'
import { isStringNotEmpty } from '../../../../../NonReact/utils/GlobalFunctions'
import { requestGetJSON } from '../../../../../NonReact/common/API/REST/RESTAPIClient'
import { LanguageContext } from '../../../../../React/Common/Contexts'
import CLanguageHandler from '../../../../../NonReact/Languages/LanguageHandler'
import { AppMenu as AppMenuLanguage } from '../../../../../../i18n'
import {
    buildNewUiSsoUrl,
    FUSION_TOKEN_QUERY_PARAM,
    NEW_UI_INVITE_DISMISSED_STORAGE_KEY
} from '../Constants'
import { NewUISsoResponse } from '../Types/Types'

// Invite toast shown once per browser profile in either the Classic CCS shell or the
// new UI's /legacy iframe (which loads CCS). All visual styling lives in
// scss/router/components/invite-toast.scss — this component only owns behaviour.
//
// Visibility gate: the toast only renders if
//   - the user-pool has a configured New-UI URL (without one, "Check it out" has
//     nowhere to navigate the user), AND
//   - localStorage[NEW_UI_INVITE_DISMISSED_STORAGE_KEY] is not set.
export const InviteToast: FC = (): ReactElement | null => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(AppMenuLanguage.NAME)

    const [isVisible, setIsVisible]: [boolean, Dispatch<SetStateAction<boolean>>] = useState<boolean>(false)
    const [newUIURL, setNewUIURL]: [Nullable<string>, Dispatch<SetStateAction<Nullable<string>>>] = useState<Nullable<string>>(null)
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    useEffect((): void => {
        // Skip entirely if already dismissed.
        if (typeof window !== 'undefined' && window.localStorage?.getItem(NEW_UI_INVITE_DISMISSED_STORAGE_KEY)) {
            return
        }

        getNewUIConfig()
            .then((config: JSONResponse<NewUIConfigDTO>): void => {
                if (!isMounted.current) return
                const configuredURL: string = config?.url?.trim() ?? EMPTY_STRING
                if (isStringNotEmpty(configuredURL)) {
                    setNewUIURL(configuredURL)
                    setIsVisible(true)
                }
            })
            .catch((): void => {
                // Silently skip — the absence of a new UI URL just means no toast.
            })
    }, [isMounted])

    const persistDismissed = useCallback((): void => {
        try {
            window.localStorage?.setItem(NEW_UI_INVITE_DISMISSED_STORAGE_KEY, '1')
        } catch {
            // localStorage may be disabled in private browsing; nothing to do.
        }
    }, [])

    const handleDismiss = useCallback((): void => {
        persistDismissed()
        setIsVisible(false)
    }, [persistDismissed])

    const handleSwitchToNewUI = useCallback((): void => {
        if (!newUIURL) return

        // Mark dismissed up-front so it doesn't reappear after the cross-window navigation.
        persistDismissed()
        setIsVisible(false)

        requestGetJSON<NewUISsoResponse>(buildNewUiSsoUrl())
            .then((pSsoResponse: JSONResponse<NewUISsoResponse>): void => {
                if (!pSsoResponse?.session_token) throw new Error('New UI SSO exchange returned no session_token')

                const pTargetURL: URL = new URL(newUIURL, window.location.origin)
                pTargetURL.searchParams.set(FUSION_TOKEN_QUERY_PARAM, pSsoResponse.session_token)
                window.top?.location.assign(pTargetURL.toString())
            })
            .catch((pError: Error): void => displayError(pError))
    }, [newUIURL, persistDismissed])

    if (!isVisible) return null

    // When CCS is loaded inside the new UI's /legacy iframe there is no outer Classic
    // top header to clear, so the toast sits closer to the top of the iframe viewport.
    const bIsEmbedded: boolean = typeof window !== 'undefined' && window.top !== window.self
    const sToastClassName: string = bIsEmbedded ? 'invite-toast invite-toast--embedded' : 'invite-toast'

    return (
        <div
            className={sToastClassName}
            role='dialog'
            aria-label={language.get(AppMenuLanguage.AppMenu_NewUIInvite_AriaLabel)}
        >
            <div className='invite-toast__body-wrapper'>
                <div className='invite-toast__heading'>
                    {language.get(AppMenuLanguage.AppMenu_NewUIInvite_Heading)}
                </div>
                <div className='invite-toast__body'>
                    {language.get(AppMenuLanguage.AppMenu_NewUIInvite_Body)}
                </div>
                <div className='invite-toast__button-row'>
                    <button
                        type='button'
                        onClick={handleSwitchToNewUI}
                        className='invite-toast__button invite-toast__button--primary'
                    >
                        {language.get(AppMenuLanguage.AppMenu_NewUIInvite_Confirm)}
                    </button>
                    <button
                        type='button'
                        onClick={handleDismiss}
                        className='invite-toast__button invite-toast__button--secondary'
                    >
                        {language.get(AppMenuLanguage.AppMenu_NewUIInvite_Dismiss)}
                    </button>
                </div>
            </div>
        </div>
    )
}
